import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, startWith, takeUntil } from 'rxjs';
import { RsfHoldPointDashboardCell, RsfHoldPointDashboardRow, RsfHoldPointDashboardSignature } from './rsf-hold-point-dashboard.models';
import { RsfHoldPointDashboardService } from './rsf-hold-point-dashboard.service';
import { User } from 'src/app/components/catalogs/user-catalog/services/user';

@Component({
  selector: 'app-rsf-hold-point-dashboard',
  templateUrl: './rsf-hold-point-dashboard.component.html',
  styleUrls: ['./rsf-hold-point-dashboard.component.scss']
})
export class RsfHoldPointDashboardComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly filterControl = new FormControl('', { nonNullable: true });
  readonly destroy$ = new Subject<void>();

  @ViewChildren('rsfRow') rsfRows!: QueryList<ElementRef<HTMLTableRowElement>>;

  rows: RsfHoldPointDashboardRow[] = [];
  filteredRows: RsfHoldPointDashboardRow[] = [];
  readonly visibleAmendmentRows = new Set<number>();
  loading = true;
  error = false;
  selectedSerialNo: string | null = null;
  private shouldScrollToSelectedRow = true;

  constructor(
    private readonly service: RsfHoldPointDashboardService,
    private readonly router: Router
  ) { }

  /** Loads the dashboard matrix and connects the requested free-text filter. */
  ngOnInit(): void {
    this.selectedSerialNo = this.service.consumeSelectedSerialNo();

    this.service.getDashboard()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: rows => {
          // Keep rows without hold points out if an older API payload includes them.
          this.rows = (rows ?? []).filter(row =>
            (row.holdPoints?.length ?? 0) > 0 || (row.amendmentHoldPoints?.length ?? 0) > 0
          );
          this.visibleAmendmentRows.clear();
          this.applyFilter(this.filterControl.value);
          this.loading = false;
          this.scrollToSelectedRow();
        },
        error: () => {
          this.loading = false;
          this.error = true;
        }
      });

    this.filterControl.valueChanges
      .pipe(
        startWith(this.filterControl.value),
        debounceTime(150),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(value => this.applyFilter(value));
  }

  /** Rechecks the rendered rows after Angular updates the dashboard table. */
  ngAfterViewInit(): void {
    this.rsfRows.changes
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.scrollToSelectedRow());
    this.scrollToSelectedRow();
  }

  /** Releases the filter subscription when the dashboard is removed. */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Centers the RSF selected in the source RSF page after its matrix row renders.
   * @returns Nothing when no selected RSF was provided or its row is not currently visible.
   */
  private scrollToSelectedRow(): void {
    if (!this.selectedSerialNo || !this.shouldScrollToSelectedRow || !this.rsfRows) {
      return;
    }

    const selectedRow = this.rsfRows.find(row =>
      row.nativeElement.getAttribute('data-rsf-serial') === this.selectedSerialNo
    );
    if (!selectedRow) {
      return;
    }

    this.shouldScrollToSelectedRow = false;
    setTimeout(() => selectedRow.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  }

  /**
   * Opens the existing RSF form route for the selected serial number.
   * @param row Dashboard row whose RSF should be opened.
   */
  openRsf(row: RsfHoldPointDashboardRow): void {
    this.router.navigate(['/redirect', row.serialNo]);
  }

  /**
   * Shows or hides the pending amendment hold-point row for an RSF.
   * @param row Dashboard row whose amendment hold points are being toggled.
   * @param visible Whether the amendment hold points should be displayed.
   */
  toggleAmendmentHoldPoints(row: RsfHoldPointDashboardRow, visible: boolean): void {
    if (visible) {
      this.visibleAmendmentRows.add(row.id);
      return;
    }

    this.visibleAmendmentRows.delete(row.id);
  }

  /**
   * Determines whether pending amendment hold points are expanded for an RSF.
   * @param row Dashboard row being checked.
   * @returns True when the amendment row is visible.
   */
  isAmendmentHoldPointsVisible(row: RsfHoldPointDashboardRow): boolean {
    return this.visibleAmendmentRows.has(row.id);
  }

  /**
   * Returns only signature slots that contribute to the hold-point count.
   * @param cell Hold-point status cell.
   * @returns Required and enabled signatures for the cell.
   */
  getRequiredSignatures(cell: RsfHoldPointDashboardCell): RsfHoldPointDashboardSignature[] {
    return cell.signatures.filter(signature => signature.required && signature.enabled);
  }

  /**
   * Builds a concise tooltip for a signature icon.
   * @param signature Signature represented by the icon.
   * @returns Tooltip text describing the signature state.
   */
  signatureTooltip(signature: RsfHoldPointDashboardSignature): string {
    const signatureLabel = signature.requesterSignature ? 'Requester signature' : (signature.title || 'Signature');
    const assignees = signature.requesterSignature
      ? (signature.users?.[0] || signature.roles?.[0] || '')
      : [...(signature.roles || []), ...(signature.users || [])]
        .filter((value, index, values) => value && values.indexOf(value) === index)
        .join(', ');
    if (signature.signed) {
      return `${signatureLabel} — signed by ${signature.signedBy || 'user'}`;
    }
    return `${signatureLabel}${assignees ? ` (${assignees})` : ''} — not signed`;
  }

  /**
   * Builds the signer shown inside the checkbox, matching checklist signature checkboxes.
   * @param signature Signature represented by the checkbox.
   * @returns The signing user with initials, or null while unsigned.
   */
  getSignatureUser(signature: RsfHoldPointDashboardSignature): User | null {
    if (!signature.signed) {
      return null;
    }
    return { id: signature.signedById, name: signature.signedBy, initials: signature.signedByInitials } as User;
  }

  /**
   * Converts the API sign date for the checkbox tooltip.
   * @param signature Signature represented by the checkbox.
   * @returns Signed date, or null while unsigned.
   */
  getSignedOn(signature: RsfHoldPointDashboardSignature): Date | null {
    return signature.signedOn ? new Date(signature.signedOn) : null;
  }

  /**
   * Supplies stable row identity to the matrix renderer.
   * @param _ Angular row index.
   * @param row Dashboard row being rendered.
   * @returns Stable RSF ID.
   */
  trackByRow(_: number, row: RsfHoldPointDashboardRow): number {
    return row.id;
  }

  /**
   * Supplies stable signature identity to the signature renderer.
   * @param _ Angular signature index.
   * @param signature Signature being rendered.
   * @returns Stable signature ID.
   */
  trackBySignature(_: number, signature: RsfHoldPointDashboardSignature): number {
    return signature.id;
  }

  /**
   * Formats the comma-separated affected-system names like the RSF side list.
   * @param value Stored location names.
   * @returns Location names with readable comma spacing.
   */
  formatLocationNames(value: string | undefined): string {
    return (value || '').replace(/,/g, ', ');
  }

  /**
   * Filters by requester, affected system, serial number, or RSF description.
   * @param value Free-text filter value.
   */
  private applyFilter(value: string): void {
    const filter = this.normalize(value);
    const matchingRows = !filter
      ? [...this.rows]
      : this.rows.filter(row => this.normalize([
        row.serialNo,
        row.requesterName,
        row.locationNames,
        this.stripHtml(row.description)
      ].join(' ')).includes(filter));

    // Keep the matrix ordered by the numeric portion of the serial number, newest first.
    this.filteredRows = matchingRows.sort((left, right) =>
      right.serialNo.localeCompare(left.serialNo, undefined, { numeric: true, sensitivity: 'base' })
    );
  }

  /**
   * Normalizes free-text filter values for case-insensitive matching.
   * @param value Filter text to normalize.
   * @returns Normalized filter text.
   */
  private normalize(value: string | undefined): string {
    return (value || '').trim().toLowerCase();
  }

  /**
   * Removes markup before including descriptions in free-text filtering.
   * @param value Description that may contain HTML markup.
   * @returns Plain-text description.
   */
  stripHtml(value: string | undefined): string {
    return (value || '').replace(/<[^>]*>/g, ' ');
  }
}
